import React, { FC, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, FloatingNotification } from '@wix/design-system';
import { usePrevious } from 'react-use';
import { HELP_LINKS } from '../../../constants/HelpLinks';
import { useFormattedBytes } from '../../../utils/hooks/useFormattedBytes';
import { useStorageQuotaBI } from '../../../utils/hooks/storageQuota/useStorageQuotaBI';
import { useSpaceStorageToast } from '../../../utils/hooks/storageQuota/useStorageSpaceToasts';
import { useStorageQuota } from '../../../utils/hooks/storageQuota/useStorageQuota';
import styles from './StorageSpaceToasts.scss';

export const StorageSpaceToasts: FC = () => {
  const { isOpen, close } = useSpaceStorageToast();
  const wasOpen = usePrevious(isOpen);

  const { formatMessage } = useIntl();
  const { storageQuotaData } = useStorageQuota();
  const { trackToastOpened, trackUpgradeButtonClick, trackLearnMoreClick } = useStorageQuotaBI(storageQuotaData);

  const usage = storageQuotaData?.usage.storage;
  const quota = storageQuotaData?.quota.storage;
  const status = storageQuotaData?.status;
  const upgradeUrl = storageQuotaData?.upgradeUrl;

  const remaining = quota - usage;

  const remainingFormatted = useFormattedBytes(remaining > 0 ? remaining : 0);
  const usageFormatted = useFormattedBytes(usage > quota ? quota : usage);

  const isWarningStatus = status === 'storageQuotaStatusWarning';

  const title = (() => {
    switch (status) {
      case 'storageQuotaStatusFull':
        return formatMessage(
          { id: upgradeUrl ? 'Storage.Media_Toast_Storage_Full' : 'Storage.Media_Toast_Full_No_Upgrade_Title' },
          { total: usageFormatted },
        );
      case 'storageQuotaStatusWarning':
        return formatMessage({ id: 'Storage.Media_Toast_Remaining_Space' }, { remaining: remainingFormatted });
    }
  })();

  const handleClickUpgradeButton = () => {
    trackUpgradeButtonClick({
      upgradeOrigin: 'toast',
    });
  };

  const handleClickLearnMoreButton = () => {
    trackLearnMoreClick({
      clickOrigin: 'toast',
    });
  };

  useEffect(() => {
    if (isOpen && !wasOpen) {
      let toastName = '';
      if (status === 'storageQuotaStatusFull') {
        toastName = upgradeUrl ? 'exceed' : 'exceed_no_upgrade_option';
      } else if (status === 'storageQuotaStatusWarning') {
        toastName = 'about_to_exceed';
      }
      trackToastOpened(toastName);
    }
  }, [isOpen, wasOpen, status, upgradeUrl, trackToastOpened]);

  return (
    isOpen && (
      <Box className={styles.wrapper}>
        <FloatingNotification
          type={!upgradeUrl ? 'standard' : 'premium'}
          onClose={close}
          showCloseButton={!!isWarningStatus}
          textButtonProps={{
            label: <FormattedMessage id="Storage.Media_Toast_Learn_More_Link" />,
            onClick: handleClickLearnMoreButton,
            href: HELP_LINKS.LEARN_MORE_STORAGE_MEDIA,
            target: '_blank',
            as: 'a',
          }}
          buttonProps={
            status !== 'storageQuotaStatusUnlimited' && upgradeUrl
              ? {
                  label: <FormattedMessage id="Storage.Media_Toast_Upgrade_Button" />,
                  as: 'a',
                  href: upgradeUrl,
                  onClick: handleClickUpgradeButton,
                  target: '_blank',
                }
              : undefined
          }
          fullWidth
          dataHook="storage-toast"
          className={styles.floatingNotification}
          text={title}
        />
      </Box>
    )
  );
};
